import { css, cx } from "@emotion/css";
import { CheckCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon, GemIcon, InfoCircleIcon, RocketIcon } from "@octopusdeploy/design-system-icons";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type * as React from "react";
import type { ButtonBasicProps, ButtonLinkProps } from "../Button";
import { Button } from "../Button";
import { DeprecatedIconButton } from "../IconButton";
export const calloutTypes = ["information", "success", "warning", "danger", "new-feature", "generic", "upsell"] as const;
export type CalloutType = (typeof calloutTypes)[number];
export interface OptionalTitleProps {
    title?: React.ReactNode;
    hideTitle: true;
}
export interface RequiredTitleProps {
    title: React.ReactNode;
    hideTitle?: false;
}
type TitleProps = OptionalTitleProps | RequiredTitleProps;
type PrimaryActionProps = Pick<ButtonBasicProps, "label" | "onClick" | "disabled"> | Pick<ButtonLinkProps, "label" | "onClick" | "href" | "external" | "disabled">;
type SecondaryActionProps = Pick<ButtonLinkProps, "label" | "href">;
type CalloutProps = TitleProps & {
    type: CalloutType;
    actions?: {
        primary: PrimaryActionProps;
        secondary?: SecondaryActionProps;
    };
    canClose?: boolean;
    onClose?: () => void;
    children?: React.ReactNode;
};
export const Callout = (props: CalloutProps) => {
    let iconElement = null;
    const containerStyles = stylesByType[props.type].container;
    const iconContainerStyles = stylesByType[props.type].iconContainer;
    const titleStyles = stylesByType[props.type].title;
    switch (props.type) {
        case "information":
            iconElement = <InfoCircleIcon size={24}/>;
            break;
        case "success":
            iconElement = <CheckCircleIcon size={24}/>;
            break;
        case "warning":
            iconElement = <ExclamationCircleIcon size={24}/>;
            break;
        case "danger":
            iconElement = <ExclamationTriangleIcon size={24}/>;
            break;
        case "new-feature":
            iconElement = <RocketIcon size={24}/>;
            break;
        case "upsell":
            iconElement = <GemIcon size={24}/>;
            break;
        case "generic":
            break;
    }
    return (<div className={containerStyles} role="alert">
            {iconElement && <div className={iconContainerStyles}>{iconElement}</div>}
            <div className={contentContainerStyles}>
                {!props.hideTitle && <div className={titleStyles}>{props.title}</div>}
                {props.children && <div className={bodyStyles}>{props.children}</div>}
                {props.actions && (<div className={actionsContainerStyles}>
                        <Button importance="loud" {...props.actions.primary}/>
                        {props.actions.secondary && <Button importance="quiet" external {...props.actions.secondary}/>}
                    </div>)}
            </div>
            {props.canClose && (<div>
                    <DeprecatedIconButton className={closeButtonStyles} accessibleName="Close Button" onClick={props.onClose} icon="XmarkIcon"/>
                </div>)}
        </div>);
};
Callout.displayName = "Callout";
const baseContainerStyles = css({
    display: "flex",
    padding: `${space[12]} ${space[16]}`,
    gap: space[8],
});
// This creates a background with a solid border on the left edge
function buildBackgroundGradient(borderColor: string, backgroundColor: string) {
    return `linear-gradient(90deg, ${borderColor} 0%, ${borderColor} ${space[4]}, ${backgroundColor} ${space[4]}, ${backgroundColor} 100%)`;
}
const baseIconContainerStyles = css({
    height: "1.5rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
});
const baseTitleStyles = css({
    display: "flex",
    alignItems: "center",
    font: text.body.bold.medium,
    lineHeight: "1.25rem", // This is needed to align to designs while the line height text token changes are not yet merged
});
const stylesByType: Record<CalloutType, Record<"container" | "iconContainer" | "title", string>> = {
    information: {
        container: cx(baseContainerStyles, css({
            background: buildBackgroundGradient(themeTokens.color.callout.border.info, themeTokens.color.callout.background.info.default),
        })),
        iconContainer: cx(baseIconContainerStyles, css({
            color: themeTokens.color.callout.icon.info,
        })),
        title: cx(baseTitleStyles, css({
            color: themeTokens.color.callout.text.info,
        })),
    },
    success: {
        container: cx(baseContainerStyles, css({
            background: buildBackgroundGradient(themeTokens.color.callout.border.success, themeTokens.color.callout.background.success.default),
        })),
        iconContainer: cx(baseIconContainerStyles, css({
            color: themeTokens.color.callout.icon.success,
        })),
        title: cx(baseTitleStyles, css({
            color: themeTokens.color.callout.text.success,
        })),
    },
    warning: {
        container: cx(baseContainerStyles, css({
            background: buildBackgroundGradient(themeTokens.color.callout.border.warning, themeTokens.color.callout.background.warning.default),
        })),
        iconContainer: cx(baseIconContainerStyles, css({
            color: themeTokens.color.callout.icon.warning,
        })),
        title: cx(baseTitleStyles, css({
            color: themeTokens.color.callout.text.warning,
        })),
    },
    danger: {
        container: cx(baseContainerStyles, css({
            background: buildBackgroundGradient(themeTokens.color.callout.border.danger, themeTokens.color.callout.background.danger.default),
        })),
        iconContainer: cx(baseIconContainerStyles, css({
            color: themeTokens.color.callout.icon.danger,
        })),
        title: cx(baseTitleStyles, css({
            color: themeTokens.color.callout.text.danger,
        })),
    },
    "new-feature": {
        container: cx(baseContainerStyles, css({
            background: buildBackgroundGradient(themeTokens.color.callout.border.feature, themeTokens.color.callout.background.feature.default),
        })),
        iconContainer: cx(baseIconContainerStyles, css({
            color: themeTokens.color.callout.icon.feature,
        })),
        title: cx(baseTitleStyles, css({
            color: themeTokens.color.callout.text.feature,
        })),
    },
    generic: {
        container: cx(baseContainerStyles, css({
            background: buildBackgroundGradient(themeTokens.color.callout.border.info, themeTokens.color.callout.background.info.default),
        })),
        iconContainer: cx(baseIconContainerStyles, css({
            color: themeTokens.color.callout.icon.info,
        })),
        title: cx(baseTitleStyles, css({
            color: themeTokens.color.callout.text.info,
        })),
    },
    upsell: {
        container: cx(baseContainerStyles, css({
            background: `${themeTokens.color.callout.border.upsell}, ${themeTokens.color.callout.background.upsell}`,
            backgroundSize: `${space[4]} 100%, cover`,
            backgroundPosition: "left top, center",
            backgroundRepeat: "no-repeat, no-repeat",
        })),
        iconContainer: cx(baseIconContainerStyles, css({
            color: themeTokens.color.callout.icon.feature,
        })),
        title: cx(baseTitleStyles, css({
            color: themeTokens.color.text.primary,
        })),
    },
};
const contentContainerStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: space[4],
    padding: `${space[2]} 0`,
    flex: 1,
});
const bodyStyles = css({
    font: text.body.regular.medium,
    color: themeTokens.color.callout.text.body,
    lineHeight: "1.25rem", // This is needed to align to designs while the line height text token changes are not yet merged
});
const actionsContainerStyles = css({
    display: "flex",
    alignItems: "center",
    alignContent: "center",
    flexWrap: "wrap",
    gap: space[16],
    paddingTop: space[12],
});
const closeButtonStyles = css({
    // We don't have a great way of customizing colors for components yet, so this is a hacky workaround
    "&.MuiIconButton-root": {
        color: themeTokens.color.icon.secondary,
    },
});
